import React, { useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/joy';
import { Plus as AddIcon } from '@phosphor-icons/react';
import type { SectionBounds } from '../../types/visualEditor';

interface DropZoneComponentProps {
  afterIndex: number;
  isActive: boolean; // Drag is happening somewhere
  isDragOver: boolean; // Drag is over this specific zone
  bounds?: SectionBounds;
  iframeScrollTop: number;
  onDrop: (sourceIndex: number) => void;
  onAddClick: () => void;
  onDragOver: () => void;
}

export const DropZone: React.FC<DropZoneComponentProps> = ({
  afterIndex,
  isActive,
  isDragOver,
  bounds,
  iframeScrollTop,
  onDrop,
  onAddClick,
  onDragOver,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!bounds) {
    return null;
  }

  // Calculate position
  const top = bounds.top - iframeScrollTop;

  // Don't render if completely off screen
  if (top < -50 || top > window.innerHeight + 50) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: top - 20, // Center the zone on the boundary
        left: bounds.left,
        width: bounds.width,
        height: isActive || isHovered ? 40 : 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'auto',
        transition: 'all 0.2s ease',
        zIndex: isDragOver ? 20 : 5,
      }}
      onMouseEnter={() => {
        setIsHovered(true);
        if (isActive) {
          onDragOver();
        }
      }}
      onMouseLeave={() => setIsHovered(false)}
      onMouseUp={() => {
        if (isActive) {
          // The actual drop handling is done in parent
        }
      }}
    >
      {/* Drop indicator line */}
      {(isActive || isHovered) && (
        <Box
          sx={{
            position: 'absolute',
            left: '10%',
            right: '10%',
            top: '50%',
            transform: 'translateY(-50%)',
            height: isDragOver ? 4 : 2,
            bgcolor: isDragOver ? 'primary.500' : isHovered ? 'primary.300' : 'neutral.300',
            borderRadius: 2,
            transition: 'all 0.15s ease',
          }}
        />
      )}

      {/* Add button - always visible on hover or when drag is over */}
      <Box
        sx={{
          opacity: isHovered || isDragOver ? 1 : 0,
          transform: isHovered || isDragOver ? 'scale(1)' : 'scale(0.8)',
          transition: 'all 0.15s ease',
        }}
      >
        <Tooltip title="Add section" placement="top" size="sm">
          <IconButton
            size="sm"
            variant="solid"
            color={isDragOver ? 'primary' : 'neutral'}
            onClick={(e) => {
              e.stopPropagation();
              onAddClick();
            }}
            sx={{
              borderRadius: '50%',
              width: 32,
              height: 32,
              boxShadow: 'md',
              bgcolor: isDragOver ? 'primary.500' : 'background.surface',
              color: isDragOver ? 'white' : 'neutral.600',
              border: '2px solid',
              borderColor: isDragOver ? 'primary.500' : 'divider',
              '&:hover': {
                bgcolor: 'var(--brand-text, #7013f5)',
                color: 'white',
                borderColor: 'primary.500',
              },
            }}
          >
            <AddIcon size={18} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};
